import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
  CustomContainer,
  Form,
  useDispatch,
} from "../../atoms/DependencyHelper";
import DeliveryTitle from "../../atoms/deliveryTitle/DeliveryTitle";
import XdsForm from "../../atoms/newLineForms/xdsForm/XdsForm";
import ResultInformation from "../../atoms/resultInformation/ResultInformation";
import CTAButton from "../../atoms/CTAButton/CTAButton";
import styles from "./CheckoutXds.module.scss";
import microService from "../../../utils/microServices";
import labels from "../../../config/labels";

import { SET_XDS_RESULT } from "../../../Redux/actionTypes/index";

import * as Analytics from "../../../services/analyticsUtag";

const CheckoutXds = forwardRef(
  (
    {
      continueDisable,
      userLoggedIn,
      profileData,
      authDetails,
      customerStatus,
      checkoutDetails,
      setShowSnackBar,
      handleHeading,
      navigate,
      multicartFlag,
    },
    ref
  ) => {
    const [apiResponse, setApiResponse] = useState(null);
    const [formContent, setFormContent] = useState(null);
    const [submitDisabled, setSubmitDisabled] = useState(true);

    const dispatch = useDispatch();

    const [showResult, setShowResult] = useState({
      visible: true,
      analyticsType: null,
      iconType: "loading",
      title: labels.xds.lbl_question_fetching_title,
      desc: null,
    });

    useImperativeHandle(ref, () => ({
      submit: () => {
        Analytics.link({
          link_type: "o",
          link_name: `checkout: authentication: ${customerStatus}: xds: continue`,
        });

        continueDisable(true);
        if (checkoutDetails.skipCreditVetting) {
          //navigate to review
          navigate("review");
        } else {
          //navigate to credit check
          navigate("creditCheck");
        }
      },
      back: () => {
        Analytics.link({
          link_type: "o",
          link_name: `checkout: authentication: ${customerStatus}: xds: back to lets get started`,
        });
      },
    }));

    const [state, setState] = useState({
      errorStatus: {
        xds: true,
      },
      fieldValues: {
        xds: {},
      },
    });

    const { errorStatus, fieldValues } = state;

    useEffect(() => {
      if (formContent && customerStatus) {
        Analytics.view({
          page_event: "view",
          page_name: `checkout: authentication: ${customerStatus}: xds`,
          page_channel: "checkout",
          page_master_tab: "personal",
          multicart_flag: multicartFlag ? true : false,
          ...Analytics.getProfileData(userLoggedIn, profileData, authDetails),
        });
      }
    }, [formContent, customerStatus]);

    /**
     * Get the question and answer of form
     */
    useEffect(() => {
      fetchQuestionData();
    }, []);

    const fetchQuestionData = async () => {
      let reqParams = [
        checkoutDetails.authDetails?.idNumber,
        checkoutDetails.authDetails?.msisdn,
      ];
      try {
        let res = await microService.get(
          "api",
          "questionsAndAnswers",
          null,
          false,
          reqParams
        );
        let response = res.response?.data;

        if (
          response?.result?.connectFraudGetQuestionsResult?.currentObjectState
        ) {
          setApiResponse(
            response.result.connectFraudGetQuestionsResult.currentObjectState
          );
        }

        if (
          response?.result?.connectFraudGetQuestionsResult?.currentObjectState
            ?.authenticationDocument?.questions?.questionDocument
        ) {
          setFormContent(
            response.result.connectFraudGetQuestionsResult.currentObjectState
              .authenticationDocument.questions.questionDocument
          );
          handleHeading("show", "desc");
          setShowResult({
            visible: false,
          });
        } else {
          setShowResult({
            visible: true,
            analyticsType: "technical error",
            iconType: "failed",
            title: labels.xds.lbl_error_title,
            desc: labels.xds.lbl_error_desc,
          });
          continueDisable(false);
        }
      } catch {
        setShowResult({
          visible: true,
          analyticsType: "technical error",
          iconType: "failed",
          title: labels.xds.lbl_error_title,
          desc: labels.xds.lbl_error_desc,
        });
        continueDisable(false);
      }
    };

    /**
     * Set the initial state of form fieldvalues
     *
     * @call when formContent state value is set
     */
    useEffect(() => {
      if (formContent) {
        let totalQuestion = formContent.length;
        for (let i = 0; i < totalQuestion; i++) {
          let questionId = formContent[i].productAuthenticationQuestionID;
          fieldValues["xds"][questionId] = "";
        }

        setState({
          ...state,
          fieldValues,
        });
      }
    }, [formContent]);

    /**
     * Form input field change event handler
     *
     * @usage call fieldValidationCheck function
     * @usage Set the state of form errorStatus
     * @usage Enable or disable form submit button
     * @call onChange event
     */
    const handleChange = async (event, name, value, form) => {
      event.persist();
      await fieldValidationCheck(name, value, form);

      errorStatus[form] = await checkFormError(form);
      setState({
        ...state,
        errorStatus,
      });

      if (errorStatus[form]) {
        setSubmitDisabled(true);
      } else {
        checkErrorStatus();
      }
    };

    /**
     * Check input field validation
     *
     * @usage Set the state of form fieldValues
     */
    const fieldValidationCheck = async (name, value, form) => {
      fieldValues[form][name] = value;

      closeSnackBar();

      setState({
        ...state,
        fieldValues,
      });
    };

    /**
     * Check entire form error
     *
     * @usage check if any form field value is empty or not
     * @return true/false
     */
    function checkFormError(form) {
      var values = Object.values(state.fieldValues[form]);

      for (const value of values) {
        if (value == "") {
          return true;
        }
      }
      return false;
    }

    /**
     * Check form error status
     *
     * @usage check if form error status is true or false
     * @usage Enable or disable form submit button
     */
    function checkErrorStatus() {
      var errValues = Object.values(state.errorStatus);

      for (const err of errValues) {
        if (err) {
          setSubmitDisabled(true);
          return;
        }
      }

      setSubmitDisabled(false);
    }

    /**
     * Form submit handler
     *
     * @usage call submitForm funtion when there is no error
     * @call onSubmit event
     */
    const handleSubmit = (event) => {
      event.preventDefault();
      event.persist();

      if (!state.errorStatus["xds"]) {
        Analytics.link({
          ...Analytics.getUtagData([
            "page_name",
            "page_channel",
            "page_master_tab",
            "visitor_login_status",
            "visitor_customer_id",
            "visitor_id_hash",
            "visitor_msisdn_hash",
            "visitor_subscription_type",
          ]),
          link_type: "o",
          link_name: `checkout: authentication: ${customerStatus}: xds: submit answers`,
        });
        submitForm();
      } else {
        displaySnackBar(
          labels.errors.lbl_form_not_submitted_fill_required,
          "form_level"
        );
        window.scrollTo(0, 0);
        event.stopPropagation();
      }
    };

    /**
     * Form submit request payload
     *
     * @usage map the key-value pair request
     */
    const mapFieldData = () => {
      let questionsObj = mapQuestionsData();

      return {
        procesAction: "AUTHENTICATE",
        myAuthenticationProcess: {
          currentObjectState: {
            authenticationDocument: {
              subscriberID: apiResponse?.authenticationDocument?.subscriberID,
              subscriberAuthenticationID:
                apiResponse?.authenticationDocument?.subscriberAuthenticationID,
              authenticationDate:
                apiResponse?.authenticationDocument?.authenticationDate,
              requiredAuthenticatedPerc:
                apiResponse?.authenticationDocument?.requiredAuthenticatedPerc,
              authenticatedPerc:
                apiResponse?.authenticationDocument?.authenticatedPerc,
              idNo: apiResponse?.authenticationDocument?.idNo,
              errorMessage: apiResponse?.authenticationDocument?.errorMessage,
              noofAttemptsRemaining:
                apiResponse?.authenticationDocument?.noofAttemptsRemaining,
              authenticationStatusReason:
                apiResponse?.authenticationDocument?.authenticationStatusReason,
              blockingReason:
                apiResponse?.authenticationDocument?.blockingReason,
              questions: {
                questionDocument: questionsObj,
              },
              userBlocked: apiResponse?.authenticationDocument?.userBlocked,
            },
            currentQuestionNum: apiResponse?.currentQuestionNum,
          },
        },
      };
    };

    /**
     * Form submit request payload
     *
     * @usage map the question key-value pair request
     */
    const mapQuestionsData = () => {
      let quesArr = formContent;
      let quesLen = quesArr.length;

      for (let i = 0; i < quesLen; i++) {
        if (
          state.fieldValues["xds"][quesArr[i].productAuthenticationQuestionID]
        ) {
          let ansLen = quesArr[i].answers.answerDocument.length;

          for (let j = 0; j < ansLen; j++) {
            if (
              quesArr[i].answers.answerDocument[j].answerID ==
              state.fieldValues["xds"][
                quesArr[i].productAuthenticationQuestionID
              ]
            ) {
              quesArr[i].answers.answerDocument[j].enteredAnswerYN = true;
              quesArr[i].answers.answerDocument[j].isEnteredAnswerYN = true;
            } else {
              quesArr[i].answers.answerDocument[j].isEnteredAnswerYN = false;
            }
          }
        }
      }
      return quesArr;
    };

    /**
     * Form submit api call
     *
     * @usage submit the form with request payload
     */
    const submitForm = async () => {
      let inputObj = mapFieldData();

      try {
        handleHeading("hide", "desc");
        setShowResult({
          visible: true,
          analyticsType: null,
          iconType: "loading",
          title: labels.xds.lbl_checking_answer_title,
          desc: null,
        });

        let responseData = await microService.post(
          "api",
          "submitXdsAnswers",
          inputObj,
          null,
          false
        );

        let reduxInfo = { authenticationStatusReason: "" };

        if (responseData.response?.data?.result?.authenticationStatusReason) {
          if (
            responseData.response?.data?.result?.authenticationStatusReason ==
            "Authenticated"
          ) {
            reduxInfo.authenticationStatusReason = "success";
            setShowResult({
              visible: true,
              analyticsType: "success",
              iconType: "success",
              title: labels.xds.lbl_success_title,
              desc: labels.xds.lbl_success_desc,
            });
          } else {
            reduxInfo.authenticationStatusReason = "failed";
            setShowResult({
              visible: true,
              analyticsType: "failed",
              iconType: "failed",
              title: labels.xds.lbl_failed_title,
              desc: labels.xds.lbl_failed_desc,
            });
          }
        } else {
          if (responseData?.error?.status === 401) {
            reduxInfo.authenticationStatusReason = "failed";
            setShowResult({
              visible: true,
              analyticsType: "technical error",
              iconType: "failed",
              title: labels.xds.lbl_failed_title,
              desc: labels.xds.lbl_failed_desc,
            });
          } else {
            reduxInfo.authenticationStatusReason = "error";
            setShowResult({
              visible: true,
              analyticsType: "technical error",
              iconType: "failed",
              title: labels.xds.lbl_error_title,
              desc: labels.xds.lbl_error_desc,
            });
          }
        }

        dispatch({
          type: SET_XDS_RESULT,
          payload: reduxInfo,
        });

        continueDisable(false);
      } catch {
        setShowResult({
          visible: true,
          analyticsType: "technical error",
          iconType: "failed",
          title: labels.xds.lbl_error_title,
          desc: labels.xds.lbl_error_desc,
        });
        dispatch({
          type: SET_XDS_RESULT,
          payload: { authenticationStatusReason: "error" },
        });
        continueDisable(false);
      }
    };

    const displaySnackBar = (message, level = "page_level") => {
      setShowSnackBar({ visible: true, message: message });

      const pageAnalyticsData = {
        page_errors: message,
        page_error_type: "page",
      };

      if (level == "form_level") {
        pageAnalyticsData.page_form_event = "error";
        pageAnalyticsData.page_form_name = `checkout: authentication: ${customerStatus}: xds`;
        pageAnalyticsData.page_error_type = "form";
      }

      Analytics.link({
        ...Analytics.getUtagData([
          "page_name",
          "page_channel",
          "page_master_tab",
          "visitor_login_status",
          "visitor_customer_id",
          "visitor_id_hash",
          "visitor_msisdn_hash",
          "visitor_subscription_type",
        ]),
        ...pageAnalyticsData,
        link_type: "o",
        link_name: `checkout: authentication: ${customerStatus}: xds: submit answers`,
      });
    };

    const closeSnackBar = () => {
      setShowSnackBar({ visible: false, message: "" });
    };

    return (
      <>
        <CustomContainer className={`${styles["container"]}`}>
          {!showResult.visible && (
            <DeliveryTitle
              contents={labels.xds.lbl_info}
              className={styles["standard-box"]}
              id={`checkout-xds-standard-delivery`}
              iconType="info"
            />
          )}
          {!showResult.visible && (
            <Form
              className={`${styles["form"]}`}
              noValidate
              id="checkout-xds-form"
              onSubmit={handleSubmit}
            >
              <XdsForm
                formContent={formContent}
                formName="xds"
                fieldValues={state.fieldValues["xds"]}
                onChangeHandler={handleChange}
              />
              <CustomContainer className={styles["button-wrapper"]}>
                <CTAButton
                  type="submit"
                  variant="solid grey"
                  disabled={submitDisabled}
                  className={styles["button"]}
                  id="checkout-xds-form-submit"
                >
                  Submit answers
                </CTAButton>
              </CustomContainer>
            </Form>
          )}
          {showResult.visible && (
            <ResultInformation
              className={styles["information"]}
              title={showResult.title}
              analyticsType={showResult.analyticsType}
              iconType={showResult.iconType}
              desc={showResult.desc}
              formName="xds"
              pageName={`checkout: authentication: ${customerStatus}`}
            />
          )}
        </CustomContainer>
      </>
    );
  }
);

CheckoutXds.propTypes = {
  continueDisable: PropTypes.func,
  userLoggedIn: PropTypes.bool,
  profileData: PropTypes.any,
  authDetails: PropTypes.any,
  customerStatus: PropTypes.string,
  navigate: PropTypes.func,
};

export default CheckoutXds;
